<template>
  <v-container fluid>
    <v-row class="mb-6">
      <v-col cols="12" class="d-flex align-center justify-space-between">
        <h2 class="text-h5 font-weight-bold">{{ $t("Permissions") }}</h2>

        <!-- A white pill inside the theme toggle's glass rim, so the two header controls
             read as one family. A plain <button> for the same reason ThemeToggle uses one:
             v-btn's own background and ripple would have to be fought with !important. -->
        <button
            type="button"
            class="save-btn"
            :class="{ 'save-btn--busy': saving }"
            :disabled="!dirtyCount || saving"
            :aria-busy="saving ? 'true' : 'false'"
            @click="saveAll"
            title="Save changes">
          <span class="save-btn-face">
            <v-progress-circular v-if="saving" indeterminate size="16" width="2" color="#0a1222"/>
            <v-icon v-else size="18" color="#0a1222">mdi-content-save-outline</v-icon>
            SAVE<span v-if="dirtyCount">&nbsp;({{ dirtyCount }})</span>
          </span>
        </button>
      </v-col>
    </v-row>

    <v-card elevation="2">
      <v-simple-table class="permission-table">
        <thead>
        <tr>
          <th class="text-left sticky-col">Panel</th>
          <th v-for="role in roles" :key="role" class="text-center">{{ getRoleName(role) }}</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="row in displayRows" :key="row.panel">
          <td class="sticky-col">
            <div class="d-flex align-center">
              <v-chip x-small class="mr-2">{{ row.panel }}</v-chip>
              <span class="font-weight-medium">{{ row.title }}</span>
            </div>
          </td>

          <td v-for="cell in row.cells" :key="row.panel + '-' + cell.role" class="py-2">
            <div class="d-flex align-center justify-center">
              <!-- Visible toggle -->
              <v-btn icon small @click="toggleVisible(cell)" :title="cell.visible ? 'Visible' : 'Hidden'">
                <v-icon>{{ cell.visible ? 'mdi-eye' : 'mdi-eye-off' }}</v-icon>
              </v-btn>

              <!-- CRUD toggles + QABUL rendered as one connected segmented control -->
              <div
                  class="d-flex align-center crud-group ml-2"
                  :class="{ 'has-qabul': row.panel === 'ARIZALAR' }"
              >
                <v-btn-toggle
                    v-model="cell._crud"
                    multiple
                    dense
                    :disabled="!cell.visible"
                    @change="markDirty(cell)"
                >
                  <v-btn x-small value="C" title="Create">C</v-btn>
                  <v-btn x-small value="R" title="Read">R</v-btn>
                  <v-btn x-small value="U" title="Update">U</v-btn>
                  <v-btn x-small value="D" title="Delete">D</v-btn>
                  <v-btn
                      v-if="row.panel === 'ARIZALAR' || row.panel === 'IMTIHON_NATIJALARI' || row.panel === 'YOTOQXONA_26_27' || (row.title && (row.title.toLowerCase().startsWith('kontrakt') || row.title.toLowerCase().startsWith('ketgan')))"
                      x-small value="E" title="Excel">EXCEL
                  </v-btn>

                  <v-btn
                      v-if="row.title && (row.title.toLowerCase().startsWith('kontrakt') || row.title.toLowerCase().startsWith('ketgan'))"
                      x-small value="A" title="Arxiv">Arxiv
                  </v-btn>
                </v-btn-toggle>

                <!-- QABUL: per-role toggle for the Accept/Reject buttons (ARIZALAR row only).
                     Backed by the hidden QABUL panel cell for the same role. Its own
                     v-btn-toggle so it inherits the exact EXCEL segment style; CSS below
                     butts it flush onto the CRUD group as one more segment. -->
                <v-btn-toggle
                    v-if="row.panel === 'ARIZALAR'"
                    dense
                    class="qabul-toggle"
                    :value="isQabulOn(cell.role) ? 'on' : null"
                    @change="toggleQabul(cell.role)"
                >
                  <v-btn x-small value="on" :title="$t('Qabul / Rad etish')">QABUL</v-btn>
                </v-btn-toggle>
              </div>

              <v-chip v-if="!cell.visible" small class="ml-2" label>Not Visible</v-chip>
              <v-chip v-if="cell._dirty" small class="ml-2" color="warning" label>changed</v-chip>
            </div>
          </td>
        </tr>
        </tbody>
      </v-simple-table>
    </v-card>
  </v-container>
</template>

<script>
import axios from "axios";

export default {
  name: "AdminPermissionMatrix",
  data: (vm) => ({
    loading: false,
    saving: false,
    roles: [],
    rows: [],
    rolesContent: [
      { id: "ADMIN_VERIFIER", name: vm.$t("Admin verifier") },
      { id: "ADMIN_OBSERVER", name: vm.$t("Admin observer") },
      { id: "ACCOUNTER", name: vm.$t("Accounter") },
      { id: "ACCOUNTER_HELPER", name: vm.$t("Accounter helper") },
      { id: "QABUL_QILUVCHI", name: vm.$t("Qabul qiluvchi") },
      { id: "BITRIX_OBSERVER", name: vm.$t("Bitrix observer") },
      { id: "BITRIX_MANAGER", name: vm.$t("Bitrix manager") },
    ],

  }),
  computed: {
    // QABUL is surfaced as a button on the ARIZALAR row, not as its own row.
    displayRows() {
      return this.rows.filter(r => r.panel !== "QABUL");
    },
    dirtyCells() {
      const out = [];
      for (const row of this.rows) {
        for (const cell of row.cells) {
          if (cell._dirty) out.push(cell);
        }
      }
      return out;
    },
    dirtyCount() {
      return this.dirtyCells.length;
    },
  },
  created() {
    this.fetchMatrix();
  },
  methods: {
    async fetchMatrix() {
      this.loading = true;
      try {
        const {data} = await axios.get("admin/panel-permissions/matrix");
        this.roles = data.roles;
        this.rows = data.rows.map(r => ({
          ...r,
          cells: r.cells.map(c => this.decorateCell(c))
        }));
      } finally {
        this.loading = false;
      }
    },

    decorateCell(cell) {
      const code = (cell.code || "").toUpperCase();
      const set = new Set(code.split("").filter(Boolean));
      return {
        ...cell,
        _crud: Array.from(set), // ['C','R','U','D']
        _dirty: false
      };
    },

    toggleVisible(cell) {
      cell.visible = !cell.visible;
      if (!cell.visible) cell._crud = []; // clear ops when hidden
      this.markDirty(cell);
    },

    markDirty(cell) {
      const order = ["C", "R", "U", "D", "E", "A"];
      const code = order.filter(x => cell._crud.includes(x)).join("");
      const was = (cell.code || "");
      cell.code = code;

      // Track original state once, then compute dirtiness
      if (cell._wasVisible === undefined) cell._wasVisible = cell.visible;
      if (cell._wasCode === undefined) cell._wasCode = was;

      cell._dirty = (cell.visible !== cell._wasVisible) || (code !== cell._wasCode);
    },

    async saveAll() {
      if (!this.dirtyCells.length) return;
      this.saving = true;
      try {
        const payload = {
          updates: this.dirtyCells.map(c => ({
            id: c.id || null,
            role: c.role,
            panel: c.panel,
            visible: c.visible,
            code: c.code || ""
          }))
        };
        const {data} = await axios.post("admin/panel-permissions/bulk", payload);

        // Merge server response (ids may be newly created)
        const byKey = new Map(data.map(x => [x.role + "::" + x.panel, x]));
        for (const row of this.rows) {
          for (const cell of row.cells) {
            const k = cell.role + "::" + cell.panel;
            const srv = byKey.get(k);
            if (srv) {
              cell.id = srv.id;
              cell.code = srv.code || "";
              cell.visible = srv.visible;
              cell._crud = (srv.code || "").split("").filter(Boolean);
            }
            cell._dirty = false;
            cell._wasVisible = cell.visible;
            cell._wasCode = cell.code;
          }
        }

        this.$emit("saved");
        this.$root.$emit("snackbar", {text: this.$t("Success"), color: "success"});
        window.location.reload();
      } catch (e) {
        console.error(e);
        this.$root.$emit("snackbar", {text: "Save failed", color: "error"});
      } finally {
        this.saving = false;
      }
    },

    // ── QABUL toggle (controls Accept/Reject visibility) — backed by the hidden QABUL panel cell ──
    qabulCell(role) {
      const qrow = this.rows.find(r => r.panel === "QABUL");
      return qrow ? qrow.cells.find(c => c.role === role) : null;
    },

    isQabulOn(role) {
      const qc = this.qabulCell(role);
      return !!(qc && qc.visible && (qc._crud || []).includes("R"));
    },

    toggleQabul(role) {
      const qc = this.qabulCell(role);
      if (!qc) return;
      if (this.isQabulOn(role)) {
        qc.visible = false;
        qc._crud = [];
      } else {
        qc.visible = true;
        qc._crud = ["R"];
      }
      this.markDirty(qc); // rebuilds qc.code, flags dirty → counted in SAVE + posted by saveAll
    },

    getRoleName(id) {
      const role = this.rolesContent.find(r => r.id === id);
      return role ? role.name : id;
    }

  }
};
</script>

<style scoped>
.permission-table thead th,
.permission-table tbody td {
  white-space: nowrap;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background: white;
}

/* Save button — built like the theme toggle: a translucent glass track with the toggle's
   rim (a 1.5px ring masked out of a pseudo-element), and a white face sitting 3px inside it
   the way the knob sits in its groove, so the white always stays within the border.
   ::before is the resting rim, ::after the brighter one that fades in on hover; two layers
   because conic gradients cannot be interpolated. */
.save-btn {
  position: relative;
  display: inline-flex;
  height: 42px;
  padding: 3px;
  border: none;
  border-radius: 999px;
  background: rgba(10, 18, 34, 0.08);
  backdrop-filter: blur(14px) saturate(180%);
  -webkit-backdrop-filter: blur(14px) saturate(180%);
  box-shadow: inset 0 1px 2px rgba(15, 20, 30, 0.12);
  color: #0a1222;
  font-family: inherit;
  cursor: pointer;
  transition: background 0.25s ease, opacity 0.2s ease;
}

.save-btn-face {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 100%;
  padding: 0 18px;
  border-radius: 999px;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: .03em;
  box-shadow:
    0 1px 3px rgba(15, 20, 30, 0.22),
    0 1px 1px rgba(15, 20, 30, 0.12);
  transition: box-shadow 0.2s ease;
}

.save-btn::before,
.save-btn::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: inherit;
  padding: 1.5px;
  pointer-events: none;
  -webkit-mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  mask:
    linear-gradient(#000 0 0) content-box,
    linear-gradient(#000 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  transition: opacity 0.2s ease;
}

.save-btn::before {
  background: conic-gradient(
    rgba(10, 18, 34, 0.3) 0deg,
    rgba(10, 18, 34, 0.3) 110deg,
    rgba(255, 255, 255, 0.95) 135deg,
    rgba(10, 18, 34, 0.3) 160deg,
    rgba(10, 18, 34, 0.3) 290deg,
    rgba(255, 255, 255, 0.95) 315deg,
    rgba(10, 18, 34, 0.3) 340deg,
    rgba(10, 18, 34, 0.3) 360deg
  );
}

.save-btn::after {
  background: conic-gradient(
    rgba(10, 18, 34, 0.38) 0deg,
    rgba(10, 18, 34, 0.38) 140deg,
    rgba(255, 255, 255, 1) 165deg,
    rgba(10, 18, 34, 0.38) 190deg,
    rgba(10, 18, 34, 0.38) 320deg,
    rgba(255, 255, 255, 1) 345deg,
    rgba(10, 18, 34, 0.38) 360deg
  );
  opacity: 0;
}

/* Brightens in place like the toggle; no lift. */
.save-btn:hover:not(:disabled) {
  background: rgba(10, 18, 34, 0.12);
}

.save-btn:hover:not(:disabled) .save-btn-face {
  box-shadow:
    0 2px 5px rgba(15, 20, 30, 0.26),
    0 1px 1px rgba(15, 20, 30, 0.14);
}

.save-btn:hover:not(:disabled)::after {
  opacity: 1;
}

/* The press reads on the face, like pressing the toggle's knob. */
.save-btn:active:not(:disabled) .save-btn-face {
  box-shadow: 0 1px 2px rgba(15, 20, 30, 0.28);
}

.save-btn:focus-visible {
  outline: 2px solid rgba(0, 39, 77, 0.5);
  outline-offset: 2px;
}

.save-btn:disabled {
  cursor: default;
  opacity: 0.5;
}

.save-btn--busy:disabled {
  cursor: progress;
  opacity: 1;
}

/* Dark theme: the face stays white, the track and rim turn to the toggle's dark-theme glass. */
.theme--dark .save-btn {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.35);
}

.theme--dark .save-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.16);
}

.theme--dark .save-btn-face {
  box-shadow:
    0 2px 6px rgba(0, 0, 0, 0.5),
    0 1px 2px rgba(0, 0, 0, 0.35);
}

.theme--dark .save-btn::before {
  background: conic-gradient(
    from 135deg,
    rgba(255, 255, 255, 0.7) 0deg,
    rgba(255, 255, 255, 0.08) 90deg,
    rgba(255, 255, 255, 0.6) 180deg,
    rgba(255, 255, 255, 0.1) 270deg,
    rgba(255, 255, 255, 0.7) 360deg
  );
}

.theme--dark .save-btn::after {
  background: conic-gradient(
    from 185deg,
    rgba(255, 255, 255, 0.9) 0deg,
    rgba(255, 255, 255, 0.14) 90deg,
    rgba(255, 255, 255, 0.8) 180deg,
    rgba(255, 255, 255, 0.16) 270deg,
    rgba(255, 255, 255, 0.9) 360deg
  );
}

.theme--dark .save-btn:focus-visible {
  outline-color: rgba(255, 255, 255, 0.5);
}

/* Without mask compositing the rims would paint as filled rectangles; fall back to a hairline. */
@supports not ((-webkit-mask-composite: xor) or (mask-composite: exclude)) {
  .save-btn::before,
  .save-btn::after {
    display: none;
  }

  .save-btn {
    box-shadow:
      inset 0 0 0 1px rgba(10, 18, 34, 0.16),
      inset 0 1px 2px rgba(15, 20, 30, 0.12);
  }

  .theme--dark .save-btn {
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.16),
      inset 0 1px 2px rgba(0, 0, 0, 0.35);
  }
}

@media (prefers-reduced-motion: reduce) {
  .save-btn,
  .save-btn::before,
  .save-btn::after,
  .save-btn-face {
    transition: none;
  }
}

/* QABUL is a separate toggle (own backing cell) but should read as one more
   segment of the CRUD group: flush against EXCEL, same style, no gap. */
.crud-group .qabul-toggle {
  margin-left: 0 !important;
}

/* Square the seam where the CRUD group meets QABUL (only when QABUL is shown). */
.crud-group.has-qabul > .v-btn-toggle:first-child,
.crud-group.has-qabul > .v-btn-toggle:first-child .v-btn:last-child {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

.crud-group.has-qabul .qabul-toggle,
.crud-group.has-qabul .qabul-toggle .v-btn {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

/* Divider between EXCEL and QABUL, matching the dividers inside the toggle. */
.crud-group.has-qabul .qabul-toggle .v-btn {
  border-left: thin solid rgba(0, 0, 0, 0.12) !important;
}
</style>